import { borderFlagsBottom, borderFlagsLeft, borderFlagsRight, borderFlagsTop, defaultBorderSettings, directionHorizontal, directionVertical } from './table';
import TableBorder from './table-border';
import TableCell from './table-cell';
import TableQuery from './table-query';
import TableRow from './table-row';

// The core table editing tool
export default class TableEditor {
  constructor (tableElement) {
    if (!tableElement || !tableElement.classList.contains('table')) {
      throw new Error('Invalid svg element passed for TableEditor');
    }

    /**
     * @type {SVGGElement}
     */
    this.tableElement = tableElement;

    /**
     * @type {HTMLCanvasElement}
     */
    this.canvas = INKIVE.EditorFacade.editor.canvas;
  }

  insertRow (rowIndex, height) {
    function shiftBordersDown (borderElement) {
      if (!borderElement) return;

      const border = new TableBorder();
      border.fromElement(borderElement);
      border.slideBy(height, directionVertical);
      const borderData = TableQuery.getBorderData(borderElement, true, false, false);
      border.setIndices(borderData.rowIndex + 1, borderData.columnIndex);
    }

    const rowIndices = [];
    const rowElements = TableQuery.getAllRowElements(this.tableElement);

    rowElements.forEach(rowElement => {
      rowIndices.push(TableQuery.getRowData(rowElement, true, false).rowIndex);
    });

    rowIndices.sort((a, b) => b - a);

    for (let r = 0; r < rowIndices.length; r++) {
      if (rowIndices[r] < rowIndex) break;

      const nextRowElement = TableQuery.getRowElementAt(this.tableElement, rowIndices[r]);
      const nextRow = new TableRow();
      nextRow.fromElement(nextRowElement);

      // Shift cells
      const nextRowCellElements = TableQuery.getAllCellElementsByRow(nextRowElement);

      nextRowCellElements.forEach(nextRowCellElement => {
        const nextRowCell = new TableCell();
        nextRowCell.fromElement(nextRowCellElement);
        const nextRowCellData = TableQuery.getCellData(nextRowCellElement, true, false, false, false);
        // Move the cell down
        nextRowCell.move(height, directionVertical);
        // Increase cell's row index
        nextRowCell.setIndices(nextRowCellData.rowIndex + 1, nextRowCellData.columnIndex);
      });

      // Last row (rowIndices is sorted from max to min, so first element is the highest one)
      const isLastRow = r === 0;
      if (isLastRow) {
        TableQuery.getRowBottomBorderElements(nextRowElement).forEach(shiftBordersDown);
      }

      // Shift borders
      TableQuery.getRowTopBorderElements(nextRowElement).forEach(shiftBordersDown);
      TableQuery.getRowSideBorderElements(nextRowElement).forEach(shiftBordersDown);

      // Shift row's index
      nextRow.setIndex(TableQuery.getRowData(nextRowElement, true, false).rowIndex + 1);
    }

    const row = new TableRow();
    row.fromData(this.tableElement, { rowIndex, height });
    return row;
  }

  deleteRow (rowIndex) {
    let height = 0;

    function shiftBordersUp (borderElement) {
      if (!borderElement) return;

      const border = new TableBorder();
      border.fromElement(borderElement);
      border.slideBy(-height, directionVertical);
      const borderData = TableQuery.getBorderData(borderElement, true, false, false);
      border.setIndices(borderData.rowIndex - 1, borderData.columnIndex);
    }

    const rowIndices = [];
    const rowElements = TableQuery.getAllRowElements(this.tableElement);
    // Don't delete the last row
    if (rowElements.length < 2) return false;

    rowElements.forEach(rowElement => {
      rowIndices.push(TableQuery.getRowData(rowElement, true, false).rowIndex);
    });

    rowIndices.sort((a, b) => a - b);

    for (let r = 0; r < rowIndices.length; r++) {
      if (rowIndices[r] < rowIndex) continue;

      const isLastRow = r === rowIndices.length - 1;

      if (rowIndices[r] === rowIndex) {
        const rowElement = TableQuery.getRowElementAt(this.tableElement, rowIndex);
        height = TableQuery.getRowData(rowElement, false, true).height;

        isLastRow || TableQuery.getRowTopBorderElements(rowElement).forEach(borderElement => borderElement && borderElement.remove());
        isLastRow && TableQuery.getRowBottomBorderElements(rowElement).forEach(borderElement => borderElement && borderElement.remove());
        TableQuery.getRowSideBorderElements(rowElement).forEach(borderElement => borderElement && borderElement.remove());

        rowElement.remove();
        continue;
      }

      const nextRowElement = TableQuery.getRowElementAt(this.tableElement, rowIndices[r]);
      const nextRow = new TableRow();
      nextRow.fromElement(nextRowElement);

      // Shift cells
      const nextRowCellElements = TableQuery.getAllCellElementsByRow(nextRowElement);

      nextRowCellElements.forEach(nextRowCellElement => {
        const nextRowCell = new TableCell();
        nextRowCell.fromElement(nextRowCellElement);
        const nextRowCellData = TableQuery.getCellData(nextRowCellElement, true, false, false, false);
        // Move the cell up
        nextRowCell.move(-height, directionVertical);
        // Decrease cell's row index
        nextRowCell.setIndices(nextRowCellData.rowIndex - 1, nextRowCellData.columnIndex);
      });

      // Shift borders
      TableQuery.getRowTopBorderElements(nextRowElement).forEach(shiftBordersUp);
      TableQuery.getRowSideBorderElements(nextRowElement).forEach(shiftBordersUp);

      if (isLastRow) {
        TableQuery.getRowBottomBorderElements(nextRowElement).forEach(shiftBordersUp);
      }

      // Shift row's index
      nextRow.setIndex(TableQuery.getRowData(nextRowElement, true, false).rowIndex - 1);
    }

    return true;
  }

  insertColumn (columnIndex, width) {
    function shiftBorderRight (borderElement) {
      if (!borderElement) return;

      const border = new TableBorder();
      border.fromElement(borderElement);
      border.slideBy(width, directionHorizontal);
      const borderData = TableQuery.getBorderData(borderElement, true, false, false);
      border.setIndices(borderData.rowIndex, borderData.columnIndex + 1);
    }

    const columnIndices = [];
    const rowCellElements = TableQuery.getAllCellElementsAtRow(this.tableElement, 0);

    rowCellElements.forEach(rowCellElement => {
      const cellData = TableQuery.getCellData(rowCellElement, true, false, true, false);
      columnIndices.push(cellData.columnIndex);
    });

    columnIndices.sort((a, b) => b - a);

    for (let c = 0; c < columnIndices.length; c++) {
      if (columnIndices[c] < columnIndex) continue;

      const columnCellElements = TableQuery.getAllCellElementsAtColumn(this.tableElement, columnIndices[c]);

      // Shift cells
      columnCellElements.forEach(columnCellElement => {
        const nextColumnCell = new TableCell();
        nextColumnCell.fromElement(columnCellElement);

        // Move the cell right
        nextColumnCell.move(width, directionHorizontal);

        // Last column (columnIndices is sorted from max to min, so first element is the highest one)
        const isLastColumn = c === 0;
        if (isLastColumn) {
          shiftBorderRight(TableQuery.getBorderElementByCell(columnCellElement, borderFlagsRight));
        }

        // Shift borders
        [
          borderFlagsLeft,
          borderFlagsTop,
          borderFlagsBottom
        ].forEach(which => shiftBorderRight(TableQuery.getBorderElementByCell(columnCellElement, which)));

        const nextRowCellData = TableQuery.getCellData(columnCellElement, true, false, false, false);
        // Increase cell's column index
        nextColumnCell.setIndices(nextRowCellData.rowIndex, nextRowCellData.columnIndex + 1);
      });
    }

    return true;
  }

  deleteColumn (columnIndex) {
    let width = 0;

    function shiftBorderLeft (borderElement) {
      if (!borderElement) return;

      const border = new TableBorder();
      border.fromElement(borderElement);
      border.slideBy(-width, directionHorizontal);
      const borderData = TableQuery.getBorderData(borderElement, true, false, false);
      border.setIndices(borderData.rowIndex, borderData.columnIndex - 1);
    }

    const columnIndices = [];
    const rowCellElements = TableQuery.getAllCellElementsAtRow(this.tableElement, 0);
    // Don't delete the last column
    if (rowCellElements.length < 2) return false;

    rowCellElements.forEach(rowCellElement => {
      const cellData = TableQuery.getCellData(rowCellElement, true, false, true, false);
      columnIndices.push(cellData.columnIndex);
      if (cellData.columnIndex === columnIndex) {
        width = cellData.width;
      }
    });

    columnIndices.sort((a, b) => a - b);

    for (let c = 0; c < columnIndices.length; c++) {
      if (columnIndices[c] < columnIndex) continue;

      const columnCellElements = TableQuery.getAllCellElementsAtColumn(this.tableElement, columnIndices[c]);

      const isLastColumn = c === columnIndices.length - 1;

      if (columnIndices[c] === columnIndex) {
        columnCellElements.forEach(columnCellElement => {
          const cell = new TableCell();
          cell.fromElement(columnCellElement);

          // Delete borders
          let borderElement = TableQuery.getBorderElementByCell(columnCellElement, borderFlagsTop);
          borderElement && borderElement.remove();

          borderElement = TableQuery.getBorderElementByCell(columnCellElement, borderFlagsBottom);
          borderElement && borderElement.remove();

          borderElement = !isLastColumn && TableQuery.getBorderElementByCell(columnCellElement, borderFlagsLeft);
          borderElement && borderElement.remove();

          borderElement = isLastColumn && TableQuery.getBorderElementByCell(columnCellElement, borderFlagsRight);
          borderElement && borderElement.remove();

          // Delete cell
          columnCellElement.remove();
        });

        continue;
      }

      // Shift cells
      columnCellElements.forEach(columnCellElement => {
        const nextColumnCell = new TableCell();
        nextColumnCell.fromElement(columnCellElement);

        // Move the cell left
        nextColumnCell.move(-width, directionHorizontal);

        // Shift borders
        [borderFlagsLeft, borderFlagsTop, borderFlagsBottom].forEach(which => shiftBorderLeft(TableQuery.getBorderElementByCell(columnCellElement, which)));
        if (isLastColumn) {
          shiftBorderLeft(TableQuery.getBorderElementByCell(columnCellElement, borderFlagsRight));
        }

        const nextColumnCellData = TableQuery.getCellData(columnCellElement, true, false, false, false);
        // Decrease cell's column index
        nextColumnCell.setIndices(nextColumnCellData.rowIndex, nextColumnCellData.columnIndex - 1);
      });
    }

    return true;
  }

  setBorderStyle (cellRowIndex, cellColumnIndex, borderSettings) {
    const cellElement = TableQuery.getCellElementAt(this.tableElement, cellRowIndex, cellColumnIndex);
    if (!cellElement) return;

    // Add borders if necessary
    this.addBorder(cellElement, borderSettings);

    const where = borderSettings.where.split('');

    let strokeWidth = borderSettings.strokeWidth;
    if (typeof strokeWidth !== 'undefined') {
      if (strokeWidth < 1) strokeWidth = 1;
      if (strokeWidth > 10) strokeWidth = 10;
    }

    let strokeDash = borderSettings.strokeDash;
    const strokeDashOffset = Number(borderSettings.strokeDashOffset);
    if (typeof strokeDash !== 'undefined' && strokeDash < 0) strokeDash = 0;

    where.forEach(borderPositionFlag => {
      const borderElement = TableQuery.getBorderElementAt(this.tableElement, cellRowIndex, cellColumnIndex, borderPositionFlag);
      if (!borderElement) return;
      borderSettings.stroke && borderElement.setAttribute('stroke', borderSettings.stroke);
      strokeWidth && borderElement.setAttribute('stroke-width', borderSettings.strokeWidth);
      strokeDash > 0 && borderElement.setAttribute('stroke-dasharray', `${strokeDash} ${strokeDash}`);
      strokeDash === 0 && borderElement.hasAttribute('stroke-dasharray') && borderElement.removeAttribute('stroke-dasharray');

      if (strokeDashOffset > 0) {
        borderElement.setAttribute('stroke-dashoffset', strokeDashOffset);
      } else if (borderElement.hasAttribute('stroke-dashoffset')) {
        borderElement.removeAttribute('stroke-dashoffset');
      }
    });
  }

  addBorder (cellElement, borderSettings) {
    const borders = [];

    let x1 = 0;
    let x2 = 0;
    let y1 = 0;
    let y2 = 0;
    let rowIndex = 0;
    let columnIndex = 0;

    const cellData = TableQuery.getCellData(cellElement, true, true, true, false);
    const stroke = borderSettings.stroke || '#000000';
    const strokeWidth = borderSettings.strokeWidth || 1;
    const strokeDash = borderSettings.strokeDash;

    const where = borderSettings.where.split('');

    where.forEach(borderPositionFlag => {
      // Prevent adding duplicated borders
      if (TableQuery.getBorderElementAt(this.tableElement, cellData.rowIndex, cellData.columnIndex, borderPositionFlag)) return;

      switch (borderPositionFlag) {
        case borderFlagsLeft:
          x1 = cellData.x;
          x2 = cellData.x;
          y1 = cellData.y;
          y2 = cellData.y + cellData.height;
          rowIndex = cellData.rowIndex;
          columnIndex = cellData.columnIndex;
          break;
        case borderFlagsRight:
          x1 = cellData.x + cellData.width;
          x2 = cellData.x + cellData.width;
          y1 = cellData.y;
          y2 = cellData.y + cellData.height;
          rowIndex = cellData.rowIndex;
          columnIndex = cellData.columnIndex + 1;
          break;
        case borderFlagsTop:
          x1 = cellData.x;
          x2 = cellData.x + cellData.width;
          y1 = cellData.y;
          y2 = cellData.y;
          rowIndex = cellData.rowIndex;
          columnIndex = cellData.columnIndex;
          break;
        case borderFlagsBottom:
          x1 = cellData.x;
          x2 = cellData.x + cellData.width;
          y1 = cellData.y + cellData.height;
          y2 = cellData.y + cellData.height;
          rowIndex = cellData.rowIndex + 1;
          columnIndex = cellData.columnIndex;
          break;
      }

      const border = new TableBorder();
      border.fromData(this.tableElement, {
        x1, x2, y1, y2, rowIndex, columnIndex, stroke, strokeWidth, strokeDash
      });

      borders.push(border);
    });

    return borders;
  }

  merge (mergeRange) {
    // Get a random merge id
    // TODO: use a better ID generator
    const id = Math.random() * 100 | 0;
    const m = `m-${id}`;
    const topLeftCellRect = this.tableElement.querySelector(`g.table-cell.index-r-${mergeRange.row[0]}.index-c-${mergeRange.column[0]} rect`);
    const bottomRightCell = this.tableElement.querySelector(`g.table-cell.index-r-${mergeRange.row[1]}.index-c-${mergeRange.column[1]} rect`);

    if (!topLeftCellRect || !bottomRightCell) return;

    for (let r = mergeRange.row[0]; r <= mergeRange.row[1]; r++) {
      for (let c = mergeRange.column[0]; c <= mergeRange.column[1]; c++) {
        const classNames = (r === mergeRange.row[0] && c === mergeRange.column[0])
          ? ['table-cell-max', m] // Mark the top-left cell
          : ['table-cell-min', m]; // Collapse remaining cells

        const cellElement = TableQuery.getCellElementAt(this.tableElement, r, c);
        classNames.forEach(className => cellElement.classList.contains(className) || cellElement.classList.add(className));
      }
    }

    let width = 0;
    let height = 0;

    for (let r = mergeRange.row[0]; r <= mergeRange.row[1]; r++) {
      for (let c = mergeRange.column[0]; c <= mergeRange.column[1]; c++) {
        const cellElement = TableQuery.getCellElementAt(this.tableElement, r, c);

        // Get total size
        const cellData = TableQuery.getCellData(cellElement, false, false, true, false);
        if (r === mergeRange.row[0]) width += cellData.width;
        if (c === mergeRange.column[0]) height += cellData.height;

        // Hide internal borders
        const borderNames = [];
        r !== mergeRange.row[0] && borderNames.push(borderFlagsTop);
        r !== mergeRange.row[1] && borderNames.push(borderFlagsBottom);
        c !== mergeRange.column[0] && borderNames.push(borderFlagsLeft);
        c !== mergeRange.column[1] && borderNames.push(borderFlagsRight);

        borderNames.forEach(name => {
          const borderElement = TableQuery.getBorderElementByCell(cellElement, name);
          borderElement && borderElement.setAttribute('stroke', 'none');
        });
      }
    }

    // Maximize the first cell
    topLeftCellRect.setAttribute('width', width);
    topLeftCellRect.setAttribute('height', height);

    return id;
  }

  split (cellElement) {
    if (!TableQuery.isMergeMaster(cellElement)) return;

    const m = TableQuery.getMergeId(cellElement);
    const cellData = TableQuery.getCellData(cellElement, true, true, false, true);
    const cellTextData = TableQuery.getCellTextData(cellElement, true, false, true);
    const cellBorderElement = TableQuery.getBorderElementByCell(cellElement, borderFlagsLeft);
    const borderData = TableQuery.getBorderData(cellBorderElement, false, false, true);

    cellElement.classList.remove('table-cell-max');
    cellElement.classList.remove(`m-${m}`);

    const minCellElements = this.tableElement.querySelectorAll(`g.table-cell-min.m-${m}`);

    minCellElements.forEach(minCellElement => {
      const cd = TableQuery.getCellData(minCellElement, true, true, false, false);

      if (cd.rowIndex === cellData.rowIndex + 1 && cd.columnIndex === cellData.columnIndex) {
        cellElement.querySelector('rect').setAttribute('height', cd.y - cellData.y);
      }

      if (cd.columnIndex === cellData.columnIndex + 1 && cd.rowIndex === cellData.rowIndex) {
        cellElement.querySelector('rect').setAttribute('width', cd.x - cellData.x);
      }

      minCellElement.classList.remove('table-cell-min');
      minCellElement.classList.remove(`m-${m}`);

      minCellElement.querySelector('rect').setAttribute('fill', cellData.fill);

      const minCell = new TableCell();
      minCell.fromElement(minCellElement);
      minCell.setTextAlignment(cellTextData.textAnchor, cellTextData.verticalAlignment);

      const minCellTextElement = minCellElement.querySelector('g.text');
      minCellTextElement.setAttribute('font-family', cellTextData.fontFamily);
      minCellTextElement.setAttribute('font-size', cellTextData.fontSize);
      minCellTextElement.setAttribute('font-weight', cellTextData.fontWeight);
      minCellTextElement.setAttribute('font-style', cellTextData.fontStyle);
      minCellTextElement.setAttribute('fill', cellTextData.fill);

      this.setBorderStyle(cd.rowIndex, cd.columnIndex, {
        where: defaultBorderSettings.where,
        stroke: borderData.stroke,
        strokeWidth: borderData.strokeWidth,
        strokeDash: borderData.strokeDash
      });
    });
  }

  /**
   *
   * @param {number} rowIndex
   * @param {number} delta
   * @param {HTMLElement} currentCellElement
   * @param {boolean} allowResizeBelowMinValue
   */
  stretchRow (rowIndex, delta, currentCellElement, allowResizeBelowMinValue = true) {
    function shiftBordersDown (borderElement) {
      if (!borderElement) return;

      const border = new TableBorder();
      border.fromElement(borderElement);
      border.slideBy(delta, directionVertical);
    }

    function stretchVerticalBorder (borderElement) {
      if (!borderElement) return;

      const border = new TableBorder();
      border.fromElement(borderElement);
      border.resizeFrom(delta, borderFlagsBottom);
    }

    const rowIndices = [];
    const rowElements = TableQuery.getAllRowElements(this.tableElement);

    rowElements.forEach(rowElement => {
      rowIndices.push(TableQuery.getRowData(rowElement, true, false).rowIndex);
    });

    rowIndices.sort((a, b) => b - a);

    for (let r = 0; r < rowIndices.length; r++) {
      if (rowIndices[r] < rowIndex) break;

      const nextRowElement = TableQuery.getRowElementAt(this.tableElement, rowIndices[r]);
      const nextRowCellElements = TableQuery.getAllCellElementsByRow(nextRowElement);

      // Last row (rowIndices is sorted from max to min, so first element is the highest one)
      const isLastRow = r === 0;

      if (rowIndices[r] === rowIndex) {
        nextRowCellElements.forEach(nextRowCellElement => {
          const nextRowCell = new TableCell();
          nextRowCell.fromElement(nextRowCellElement);
          // Move the cell down
          const isCurrentCell = nextRowCellElement === currentCellElement;
          nextRowCell.resize(delta, directionVertical, isCurrentCell, true, allowResizeBelowMinValue);
        });

        if (isLastRow) {
          TableQuery.getRowBottomBorderElements(nextRowElement).forEach(shiftBordersDown);
        }

        // Stretch borders
        TableQuery.getRowSideBorderElements(nextRowElement).forEach(stretchVerticalBorder);
        continue;
      }

      // Shift cells
      nextRowCellElements.forEach(nextRowCellElement => {
        const nextRowCell = new TableCell();
        nextRowCell.fromElement(nextRowCellElement);
        // Move the cell down
        nextRowCell.move(delta, directionVertical);
      });

      if (isLastRow) {
        TableQuery.getRowBottomBorderElements(nextRowElement).forEach(shiftBordersDown);
      }

      // Shift borders
      TableQuery.getRowTopBorderElements(nextRowElement).forEach(shiftBordersDown);
      TableQuery.getRowSideBorderElements(nextRowElement).forEach(shiftBordersDown);
    }
  }
}
